import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { activity as seedActivity, projects as seedProjects, templates as seedTemplates } from '../data/projects';
import { deliverableStage } from '../utils/deliverables';
import { applyStoredOrder } from '../utils/order';
import { authenticateStaff, changeStaffPassword, clearStaffSession, loadStaffSession, completeStaffInvite, completeStaffPasswordReset, establishInviteSession, requestStaffPasswordReset, verifyStaffRecoveryCode, type StaffSession } from '../utils/staffAuth';
import { loadClientName } from '../utils/clientIdentity';
import { slugFromPath } from '../utils/links';
import { supabase, supabaseConfigured } from '../lib/supabase';
import { fetchApprovals, fetchChangeRequests, fetchStaffActivity } from '../utils/sharedReview';
import type { Activity, ClientAlert, Deliverable, Project, ProjectTemplate, Show } from '../types/portal';
import { ADMIN_PATH, CLIENT_PATH, STAFF_PATH } from './portalPaths';
import { EMPTY_PROJECT, clock, isReleased, stamp, uid } from './portalHelpers';
import type { ClientLoad, ClientRoute, NavSnapshot, NeedsAttention, PortalRoute, PortalState, ViewMode } from './portalTypes';
import { buildClientActions, usePortalClient } from './usePortalClient';
import { buildProjectActions, usePortalProjects } from './usePortalProjects';
import { buildDeliverableActions } from './usePortalDeliverables';

/*
 * The Portal provider: owns all state and the session, and composes the
 * context value. Split across several files so none grows past ~900 lines —
 *
 *   portalTypes.ts           the context value interface and its input types
 *   portalPaths.ts           route constants
 *   portalHelpers.ts         small pure helpers (clock, stamp, uid, …)
 *   usePortalProjects.ts     project reloading + project/show/team actions
 *   usePortalDeliverables.ts deliverable, review and transcript actions
 *   usePortalClient.ts       client link loading, preview, client actions
 *
 * Every public name is re-exported below, so every existing import from this
 * path keeps working unchanged.
 */

export type { ViewMode, ClientRoute, PortalRoute, ClientLoad, NewDeliverableInput, NeedsAttention } from './portalTypes';
export { CLIENT_PATH, STAFF_PATH, ADMIN_PATH, STAFF_SETUP_PATH, STAFF_RESET_PATH, PREVIEW_PARAM } from './portalPaths';
const PortalContext = createContext<PortalState | null>(null);
export function PortalProvider({
  children
}: {
  children: React.ReactNode;
}) {
  /** A remembered name is all it takes to walk back into the project. */
  const [authed, setAuthed] = useState(() => Boolean(loadClientName()));
  const [viewMode, setViewModeState] = useState<ViewMode>('client');
  const [clientRoute, setClientRoute] = useState<ClientRoute>('home');
  const [portalRoute, setPortalRoute] = useState<PortalRoute>('projects');
  /* No transcript is ever restored from this browser: transcript_versions is
     the source of truth and is read on load. */
  const [projects, setProjects] = useState<Project[]>(() => applyStoredOrder(seedProjects));
  const [templates, setTemplates] = useState<ProjectTemplate[]>(seedTemplates);
  const [shows, setShows] = useState<Show[]>([]);
  const [activeShowId, setActiveShowId] = useState<string>('');
  /* Kept separate from activeShowId, which is a client-side concept tied to
     cameFromShow/goBackToShow — this is staff opening the Show Admin page. */
  const [activeAdminShowId, setActiveAdminShowId] = useState<string>('');
  const [activeProjectId, setActiveProjectId] = useState<string>('bloomberg');
  const [activeDeliverableId, setActiveDeliverableId] = useState<string>('d-ep02');
  /** Remembered in this browser, so the name is asked for only once. */
  const [clientName, setClientName] = useState<string>(() => loadClientName());
  const [activity, setActivity] = useState<Activity[]>(seedActivity);
  const [clientAlerts, setClientAlerts] = useState<ClientAlert[]>([]);
  const [focusDeliverableId, setFocusDeliverableId] = useState<string>('');
  const [toast, setToast] = useState<string | null>(null);
  const [path, setPath] = useState<string>(() => typeof window === 'undefined' ? '/' : window.location.pathname);
  /* Kept alongside the path so ?preview=1 on a client link is observable. */
  const [search, setSearch] = useState<string>(() => typeof window === 'undefined' ? '' : window.location.search);
  /** Never left blank: a client screen always knows why it has no project. */
  const [clientLoad, setClientLoad] = useState<ClientLoad>('idle');
  /** Restored on load so a refresh does not sign staff out mid-session. */
  const [staffSession, setStaffSession] = useState<StaffSession | null>(null);
  /** True until the persisted Supabase session has been checked. */
  const [staffChecking, setStaffChecking] = useState(true);

  /**
   * Browser back/forward support for Portal OS.
   *
   * Reported 11 Sep 2026: pressing the browser's back button while inside
   * Portal OS didn't step back one screen — it exited the whole app straight
   * to whatever page was open before Portal OS was ever loaded (e.g. a
   * client on a video's review page, one back-press, landed on Google).
   * Root cause: every screen here — Home, Deliverables, one deliverable's
   * review page, a Show, Team, a staff project's admin view, and so on — is
   * reached purely by changing React state (`clientRoute`/`portalRoute`,
   * plus which project/deliverable/show is active); nothing about that ever
   * touched `window.history`, so the browser had no entries of ours to walk
   * back through and the very first back-press always landed on whatever
   * real page preceded Portal OS in that tab.
   *
   * Fix: treat that same handful of state values as one snapshot, and keep
   * the browser's history in step with it — pushing a new entry (same URL,
   * richer state) every time the snapshot changes, and restoring it
   * wholesale on `popstate`. `restoringRef` stops a restore from turning
   * around and pushing a duplicate entry for itself. This is additive to
   * `navigate()` below (which still pushes a real URL change for switching
   * between client/staff mode) — when the URL just changed, this replaces
   * that entry's state instead of adding a second one for the same click.
   */
  const restoringFromHistoryRef = useRef(false);
  const lastSyncedPathRef = useRef<string>(typeof window === 'undefined' ? '' : window.location.pathname);
  const hasSyncedHistoryOnceRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const snapshot: NavSnapshot = {
      __portalNav: true,
      viewMode,
      clientRoute,
      portalRoute,
      activeProjectId,
      activeDeliverableId,
      activeShowId,
      activeAdminShowId
    };

    /* This change came FROM restoring a popstate event, not a fresh
       navigation — the history entry for it already exists exactly as it
       should. Just clear the flag and move on. */
    if (restoringFromHistoryRef.current) {
      restoringFromHistoryRef.current = false;
      lastSyncedPathRef.current = window.location.pathname;
      return;
    }
    const pathChanged = window.location.pathname !== lastSyncedPathRef.current;
    lastSyncedPathRef.current = window.location.pathname;
    if (!hasSyncedHistoryOnceRef.current) {
      /* The very first snapshot describes whatever entry the browser already
         made loading this page — nothing to push yet, just label it. */
      hasSyncedHistoryOnceRef.current = true;
      window.history.replaceState(snapshot, '', window.location.href);
      return;
    }
    if (pathChanged) {
      /* `navigate()` already pushed a fresh entry (with no state) for this
         transition — fill it in rather than adding a second entry for the
         same click. */
      window.history.replaceState(snapshot, '', window.location.href);
    } else {
      window.history.pushState(snapshot, '', window.location.href);
    }
  }, [viewMode, clientRoute, portalRoute, activeProjectId, activeDeliverableId, activeShowId, activeAdminShowId]);

  /* Supabase persists the session, so a refresh restores it — provided the
     user is still in staff_users with an eligible role. */
  useEffect(() => {
    let live = true;
    void loadStaffSession().then(session => {
      if (live && session) setStaffSession(session);
    }).finally(() => {
      if (live) setStaffChecking(false);
    });
    return () => {
      live = false;
    };
  }, []);

  /* After that first check, Supabase's own auth events keep the session
     honest — a sign-out in another tab, or an expired refresh token. */
  useEffect(() => {
    if (!supabase) return;
    const {
      data
    } = supabase.auth.onAuthStateChange(event => {
      if (event === 'SIGNED_OUT') setStaffSession(null);
    });
    return () => data.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    const onPop = (event: PopStateEvent) => {
      setPath(window.location.pathname);
      setSearch(window.location.search);

      /* A history entry this session itself created (see the history-sync
         effect above) carries a full navigation snapshot — restore it
         wholesale rather than leaving clientRoute/portalRoute/the active
         project stuck wherever they happened to be. An entry from before
         Portal OS was ever loaded (or from before this feature existed)
         has no such marker, and is simply left to the browser/the
         slug-resolution effect below, exactly as before. */
      const state = event.state as Partial<NavSnapshot> | null;
      if (state && state.__portalNav) {
        restoringFromHistoryRef.current = true;
        /* Belt-and-braces: if this snapshot happens to be identical to the
           current one (so the effect above never re-runs to clear the
           flag), don't leave it stuck set for the next real navigation. */
        setTimeout(() => {
          restoringFromHistoryRef.current = false;
        }, 0);
        setViewModeState(state.viewMode ?? 'client');
        setClientRoute(state.clientRoute ?? 'home');
        setPortalRoute(state.portalRoute ?? 'projects');
        if (state.activeProjectId !== undefined) setActiveProjectId(state.activeProjectId);
        if (state.activeDeliverableId !== undefined) setActiveDeliverableId(state.activeDeliverableId);
        if (state.activeShowId !== undefined) setActiveShowId(state.activeShowId);
        if (state.activeAdminShowId !== undefined) setActiveAdminShowId(state.activeAdminShowId);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /**
   * A client link names its own project: /project/bloomberg-economy-of-scale.
   * The older /client?project=<id> form still works.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (slugFromPath(window.location.pathname)) return;
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('project') || params.get('p');
    if (requested) setActiveProjectId(requested);
  }, []);

  /* Whatever project the current client link names is the project on screen. */
  useEffect(() => {
    const slug = slugFromPath(path);
    if (!slug) return;
    const matched = projects.find(item => item.slug === slug);
    if (matched) setActiveProjectId(matched.id);
  }, [path, projects]);
  const navigate = useCallback((next: string) => {
    if (typeof window !== 'undefined' && window.location.pathname !== next) {
      window.history.pushState({}, '', next);
      setSearch('');
    }
    setPath(next);
  }, []);
  const notify = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(current => current === message ? null : current), 2600);
  }, []);
  const patchProject = useCallback((projectId: string, patch: Partial<Project>) => {
    setProjects(list => list.map(item => item.id === projectId ? {
      ...item,
      ...patch,
      updated: stamp()
    } : item));
  }, []);
  const mapDeliverables = useCallback((projectId: string, fn: (list: Deliverable[]) => Deliverable[]) => {
    setProjects(list => list.map(item => item.id === projectId ? {
      ...item,
      deliverables: fn(item.deliverables),
      updated: stamp()
    } : item));
  }, []);

  /* With no projects yet, a blank one keeps every screen on its empty state. */
  const activeProject = projects.find(item => item.id === activeProjectId) ?? projects[0] ?? EMPTY_PROJECT;
  const activeShow = shows.find(item => item.id === activeShowId) ?? null;
  const activeAdminShow = shows.find(item => item.id === activeAdminShowId) ?? null;
  /** Every episode of `activeShow`, for the client-side show landing page and
      for deciding whether a "back to show" link has anywhere useful to go —
      a show with only one episode is entered directly (see the show-link
      bootstrap effect below), so there is never a one-item list to return to. */
  const showProjectsForClient = activeShowId ? projects.filter(item => item.showId === activeShowId) : [];

  /**
   * Change requests and approvals are shared, so they are read back from
   * Supabase and merged into the project on screen. Local state stays the
   * source for everything else, and remains intact if the fetch is blocked.
   */
  const syncSharedReview = useCallback(async (project: Project, viewer: string) => {
    if (!supabaseConfigured) return;
    const [remoteRequests, remoteApprovals] = await Promise.all([fetchChangeRequests(project.slug, viewer), fetchApprovals(project.slug, viewer)]);
    if (!remoteRequests.length && !remoteApprovals.length) return;
    setProjects(list => list.map(item => {
      if (item.id !== project.id) return item;
      return {
        ...item,
        deliverables: item.deliverables.map(deliverable => {
          const requests = remoteRequests.filter(row => row.deliverableKey === deliverable.id);
          /* Stored rows are authoritative on `resolved`, so a request
             completed elsewhere stops counting as outstanding here. */
          const merged = [...deliverable.changeRequests.map(local => {
            const remote = requests.find(row => row.id === local.id);
            return remote ? {
              ...local,
              resolved: remote.resolved,
              shared: true
            } : local;
          }), ...requests.filter(row => !deliverable.changeRequests.some(local => local.id === row.id))];
          const approvalHere = remoteApprovals.find(row => row.deliverableKey === deliverable.id && (!row.version || row.version === deliverable.version));
          const approvedHere = Boolean(approvalHere);
          const outstanding = merged.some(row => row.version === deliverable.version && !row.resolved);

          /* Outstanding feedback outranks an approval; otherwise a stored
             approval of the current version means exactly that. */
          let status = deliverable.status;
          const inReview = deliverableStage(deliverable.status) === 'Review';
          if (outstanding && inReview) status = 'Changes Requested';else if (approvedHere && inReview) status = 'Approved';
          return {
            ...deliverable,
            changeRequests: merged,
            status,
            approvedBy: approvalHere?.displayName ?? deliverable.approvedBy,
            approvedAt: approvalHere?.at ?? deliverable.approvedAt
          };
        })
      };
    }));
  }, []);

  /* Project and show reloading — reloadProjects, reloadShows and the effect
     that runs them. Same hooks, same order, same dependency arrays as when
     they sat inline here. See usePortalProjects.ts. */
  const {
    reloadProjects,
    reloadShows
  } = usePortalProjects({
    setProjects,
    setShows,
    staffSession,
    authed
  });

  /* Client link loading and Preview as Client — adoptClientProject,
     loadClientProject, adoptClientProjects, and the preview and bootstrap
     effects. Same hooks, same order, same dependency arrays as when they sat
     inline here. See usePortalClient.ts. */
  const {
    loadClientProject,
    previewRequested,
    clientPreview
  } = usePortalClient({
    path,
    search,
    staffSession,
    authed,
    clientName,
    setProjects,
    setActiveProjectId,
    setClientLoad,
    setActiveShowId,
    setShows,
    setClientRoute
  });

  /* Studio activity comes from the stored change requests and approvals. */
  useEffect(() => {
    if (!staffSession || !supabaseConfigured) return;
    let live = true;
    void fetchStaffActivity().then(rows => {
      if (!live || rows.length === 0) return;
      setActivity(rows.map(row => {
        const project = projects.find(item => item.slug === row.projectSlug);
        return {
          id: row.id,
          projectId: project?.id ?? row.projectSlug,
          projectName: row.projectName || (project?.client ?? ''),
          actorName: row.actorName,
          action: row.action,
          deliverableId: row.deliverableKey,
          message: row.message,
          at: row.at
        };
      }));
    });
    /* The dashboard summarises every project, so each one is reconciled with
       its stored requests and approvals rather than only the open one. */
    projects.filter(item => !item.archived).forEach(item => {
      void syncSharedReview(item, '');
    });
    return () => {
      live = false;
    };
    // Refreshed each time a staff session is established, i.e. on load.
  }, [staffSession]);

  /* Pick up anything submitted from another browser or device. Transcripts
     are not synced here: they load with the project, and the transcript page
     reads the stored revisions directly. One path, not two. */
  useEffect(() => {
    void syncSharedReview(activeProject, clientName);
  }, [activeProject.id, activeProject.slug, clientName, syncSharedReview]);
  const value = useMemo<PortalState>(() => {
    const projectId = activeProject.id;
    /* One shared link per project: whoever is at the keyboard is named here. */
    const viewerName = clientName.trim() || 'Client';
    const log = (actorName: string, action: string, deliverableId: string) => {
      setActivity(list => [{
        id: uid('act'),
        projectId,
        projectName: activeProject.client,
        actorName,
        action,
        deliverableId,
        at: Date.now()
      }, ...list]);
    };
    const patchDeliverable = (id: string, patch: Partial<Deliverable>) => {
      const before = activeProject.deliverables.find(item => item.id === id);
      mapDeliverables(projectId, list => list.map(item => item.id === id ? {
        ...item,
        ...patch
      } : item));
      if (before) {
        const after = {
          ...before,
          ...patch
        };
        if (!isReleased(before) && isReleased(after)) {
          setClientAlerts(list => [...list, {
            id: uid('alert'),
            projectId,
            kind: 'files',
            title: 'Final files are ready',
            body: `${after.name} is available to download.`,
            actionLabel: 'View Files',
            deliverableId: id
          }]);
        }
      }
    };
    const appendNote = (id: string, author: 'Client' | 'Portal' | 'System', body: string, authorName?: string, extra: Partial<Deliverable> = {}, timecode?: string) => mapDeliverables(projectId, list => list.map(item => item.id === id ? {
      ...item,
      ...extra,
      notes: [...item.notes, {
        id: uid('n'),
        author,
        authorName,
        timecode,
        time: clock(),
        body
      }]
    } : item));
    return {
      /* authed, clientName, signIn, changeClientName, contactPortal and the
         preview/permission flags. See usePortalClient.ts. */
      ...buildClientActions({
        authed,
        clientName,
        path,
        activeProject,
        projectId,
        viewerName,
        clientPreview,
        previewRequested,
        clientLoad,
        setClientName,
        setAuthed,
        setViewModeState,
        setClientRoute,
        setActiveShowId,
        setActivity,
        loadClientProject
      }),
      path,
      navigate,
      inStaffArea: path.startsWith(STAFF_PATH) || path.startsWith(ADMIN_PATH),
      staffAuthed: Boolean(staffSession),
      staffChecking,
      staffUser: staffSession,
      staffSignIn: async (email, password) => {
        const result = await authenticateStaff(email, password);
        if (result.ok && result.session) {
          setStaffSession(result.session);
          setViewModeState('portal');
          setPortalRoute('projects');
          navigate(ADMIN_PATH);
        }
        return result;
      },
      staffSignOut: async () => {
        await clearStaffSession();
        setStaffSession(null);
        setPortalRoute('projects');
        navigate(STAFF_PATH);
      },
      staffResetPassword: email => requestStaffPasswordReset(email),
      staffVerifyRecoveryCode: (email, code) => verifyStaffRecoveryCode(email, code),
      staffCompletePasswordReset: (password, confirm) => completeStaffPasswordReset(password, confirm),
      staffChangePassword: (current, password, confirm) => staffSession ? changeStaffPassword(staffSession.email, current, password, confirm) : Promise.resolve({
        ok: false,
        error: 'Not signed in.'
      }),
      staffCheckInvite: () => establishInviteSession(),
      staffAcceptInvite: async (password, confirm) => {
        const result = await completeStaffInvite(password, confirm);
        if (result.ok && result.session) {
          setStaffSession(result.session);
          setViewModeState('portal');
          setPortalRoute('projects');
          navigate(ADMIN_PATH);
        }
        return result;
      },
      goStaffAccess: () => {
        setPortalRoute('projects');
        navigate(STAFF_PATH);
      },
      viewMode,
      setViewMode: mode => {
        setViewModeState(mode);
        if (mode === 'portal') {
          setPortalRoute('projects');
          navigate(staffSession ? ADMIN_PATH : STAFF_PATH);
        } else {
          setClientRoute('home');
          navigate(CLIENT_PATH);
        }
      },
      clientRoute,
      goClient: setClientRoute,
      portalRoute,
      goPortal: setPortalRoute,
      /* Projects, shows, project settings, shoots and the Portal team. See
         usePortalProjects.ts. */
      ...buildProjectActions({
        projects,
        projectId,
        activeProject,
        templates,
        shows,
        activeShow,
        activeShowId,
        activeAdminShow,
        activeAdminShowId,
        showProjectsForClient,
        setProjects,
        setTemplates,
        setShows,
        setActiveProjectId,
        setActiveDeliverableId,
        setFocusDeliverableId,
        setActiveAdminShowId,
        setPortalRoute,
        setClientRoute,
        patchProject,
        notify,
        reloadProjects,
        reloadShows
      }),
      /* Deliverables, client review, publishing and transcripts. See
         usePortalDeliverables.ts. */
      ...buildDeliverableActions({
        projectId,
        activeProject,
        projects,
        activeDeliverableId,
        clientPreview,
        viewerName,
        staffSession,
        setActiveProjectId,
        setActiveDeliverableId,
        setClientRoute,
        setClientAlerts,
        mapDeliverables,
        patchDeliverable,
        appendNote,
        log,
        notify,
        reloadProjects
      }),
      activity,
      openActivity: entry => {
        setActiveProjectId(entry.projectId);
        setFocusDeliverableId(entry.deliverableId);
        setPortalRoute('project');
      },
      focusDeliverableId,
      needsAttention: projects.reduce<NeedsAttention>((totals, item) => {
        if (item.archived) return totals;
        item.deliverables.forEach(deliverable => {
          if (deliverable.status === 'Ready for Review' && deliverable.visibleToClient) {
            totals.reviewsWaiting += 1;
          }
          totals.changeRequests += deliverable.changeRequests.filter(request => !request.resolved).length;
          if ((deliverable.status === 'In Progress' || deliverable.status === 'Changes Requested') && deliverable.changeRequests.every(request => request.resolved)) {
            totals.readyToPublish += 1;
          }
        });
        return totals;
      }, {
        reviewsWaiting: 0,
        changeRequests: 0,
        readyToPublish: 0
      }),
      clientAlerts: clientAlerts.filter(alert => alert.projectId === projectId),
      dismissAlert: id => setClientAlerts(list => list.filter(alert => alert.id !== id)),
      toast,
      notify,
      reloadProjects
    };
  }, [authed, path, clientPreview, previewRequested, clientLoad, navigate, staffSession, staffChecking, reloadProjects, reloadShows, loadClientProject, viewMode, clientRoute, portalRoute, projects, templates, shows, activeShowId, activeShow, activeAdminShowId, activeAdminShow, activeProject, activeDeliverableId, clientName, activity, clientAlerts, focusDeliverableId, toast, notify, patchProject, mapDeliverables]);
  return <PortalContext.Provider value={value} data-id="element-1873" data-mp="3w1vazzaygug0">{children}</PortalContext.Provider>;
}
export function usePortal(): PortalState {
  const context = useContext(PortalContext);
  if (!context) throw new Error('usePortal must be used inside PortalProvider');
  return context;
}